import PushPinIcon from "@mui/icons-material/PushPin";
import { Badge, Box, Tooltip } from "@mui/material";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Flex from "../../../../../../components/Flex";
import { useRoleController } from "../../../../../../components/useRoleController";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "../../../../../../components/app/provider/patient/chart";
import useFcmSmsEventListener from "../../../../../../services/fcm/listeners/FcmSmsEventListener";
import { roleFilter } from "../../../../../../utils/utils";
import { PATIENT_CHART_TABS, TABS_COUNT_MAPPER } from "./patient-constant";

type PatientTabItemProps = {
  tab: any;
  activeTab: string;
  onTabClick: () => void;
  onTabDoubleClick: () => void;
  showPin: boolean;
};

const PatientTabItem = ({
  tab,
  activeTab,
  onTabClick,
  onTabDoubleClick,
  showPin,
}: PatientTabItemProps) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tab.label]);

  return (
    <Tooltip
      title={tab.label}
      arrow
      placement="bottom"
      disableHoverListener={!isTruncated}
      disableFocusListener={!isTruncated}
      disableTouchListener={!isTruncated}
    >
      <span>
        <Tab
          active={tab.value === activeTab}
          onTabClick={onTabClick}
          onTabDoubleClick={onTabDoubleClick}
        >
          <Flex
            position={"relative"}
            flexDirection={"column"}
            alignItems="center"
            gap={CHART_DIMENSIONS.tabGap}
            sx={{ overflow: "visible", width: "100%", minWidth: 0 }}
          >
            <Box sx={{ position: "relative" }}>
              <tab.icon
                color={CHART_COLORS.primary}
                sx={{ fontSize: CHART_DIMENSIONS.tabIconSize }}
              />
              {tab.value === "communication" && (
                <Badge
                  variant="dot"
                  badgeContent={TABS_COUNT_MAPPER[tab.value]}
                  color="error"
                  sx={{
                    right: CHART_DIMENSIONS.commBadgeRight,
                    top: CHART_DIMENSIONS.commBadgeTop,
                    "& .MuiBadge-badge": {
                      fontSize: CHART_DIMENSIONS.commBadgeFontSize,
                      backgroundColor: CHART_COLORS.statusLive,
                      color: CHART_COLORS.surfaceWhite,
                    },
                  }}
                />
              )}
            </Box>
            <Box
              ref={labelRef}
              component="span"
              sx={{
                ...CHART_TYPOGRAPHY.body5,
                fontFamily: CHART_TYPOGRAPHY.fontFamily,
                fontWeight:
                  tab.value === activeTab
                    ? CHART_TYPOGRAPHY.weight.medium
                    : CHART_TYPOGRAPHY.weight.regular,
                color: CHART_COLORS.primary,
                textAlign: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
                maxWidth: "100%",
                display: "block",
              }}
            >
              {tab.label}
            </Box>

            {showPin && (
              <Box
                position={"absolute"}
                zIndex={99}
                top={CHART_DIMENSIONS.pinIndicatorOffset}
                right={CHART_DIMENSIONS.pinIndicatorOffset}
                width={CHART_DIMENSIONS.pinIndicatorSize}
                height={CHART_DIMENSIONS.pinIndicatorSize}
                sx={{
                  backgroundColor: CHART_COLORS.bgSecondaryBlue,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: CHART_DIMENSIONS.pinIndicatorBorder,
                }}
              >
                <PushPinIcon
                  sx={{
                    fontSize: CHART_DIMENSIONS.pinIconSize,
                    color: CHART_COLORS.primary,
                    transform: "rotate(45deg)",
                  }}
                />
              </Box>
            )}
          </Flex>
        </Tab>
      </span>
    </Tooltip>
  );
};

const PatientChartTabs = () => {
  const [activeTab, setActiveTab] = useState<string>("demographics");
  const [isOnVisitNotePage, setIsOnVisitNotePage] = useState(false);
  const navigate = useNavigate();
  const { patientId } = useParams();

  /** listen to firebase sms notifications */
  const [smsCount] = useFcmSmsEventListener({});

  TABS_COUNT_MAPPER["communication"] = Number(smsCount ?? "0");

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isVisitNote = currentPath.includes("/visit-note");
    setIsOnVisitNotePage(isVisitNote);

    // On the visit note page the highlighted tab follows the pinned panel
    // (activePin) rather than the route, so it stays in sync when a panel is
    // pinned from elsewhere (e.g. "View All" in Quick Health Records).
    if (isVisitNote) {
      setActiveTab(searchParams.get("activePin") ?? "index");
      return;
    }

    setActiveTab(
      PATIENT_CHART_TABS?.find(
        (it) => it.value === location.pathname?.split("/")?.[6]
      )?.value ?? "index"
    );
  }, [location.pathname, searchParams]);

  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  const excludePinTabs: string[] = [];

  function handleTabChange(curTab: any, isDoubleClick: boolean = false) {
    if (!(isOnVisitNotePage || isVisitNotePage) || isDoubleClick) {
      setActiveTab(curTab.value);
      navigate(
        `/app/provider/patients/new-patient/${patientId}/${curTab.value}`
      );
      setIsOnVisitNotePage(false);
      return;
    }

    const isExludePinTab = excludePinTabs.includes(curTab.value);

    if (isExludePinTab) return;

    if (!isExludePinTab) {
      // Only reached while the encounter is open. Replaced rather than pushed:
      // a pushed entry keeps the same pathname, so the encounter's navigation
      // guard waves it through and every pin the provider opened has to be
      // backed out of one press at a time before the back button leaves the
      // encounter.
      setSearchParams(
        (prev) => {
          prev.set("activePin", curTab.value);
          return prev;
        },
        { replace: true }
      );
      setActiveTab(curTab.value);
    }
  }

  const { hasView } = useRoleController("Task");

  const activePinTab = searchParams?.get("activePin");

  return (
    <TabGroup
      tabs={roleFilter({
        list: PATIENT_CHART_TABS,
        compWith: { tasks: hasView },
      })}
      activeTab={activeTab}
      renderTab={(tab, activeTab) => (
        <PatientTabItem
          key={tab.value}
          tab={tab}
          activeTab={activeTab}
          onTabClick={() => handleTabChange(tab)}
          onTabDoubleClick={() => handleTabChange(tab, true)}
          showPin={
            (isOnVisitNotePage || isVisitNotePage) && tab.value === activePinTab
          }
        />
      )}
    />
  );
};

export default PatientChartTabs;

type TabGroupProps = {
  tabs: any[];
  renderTab: (tab: any, activeTab: string) => ReactNode;
  activeTab: string;
};

const TabGroup = ({ tabs, renderTab, activeTab }: TabGroupProps) => {
  return (
    <Flex
      width="100%"
      sx={{
        overflowX: "auto",
        flexShrink: 0,
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {tabs.map((tab) => renderTab(tab, activeTab))}
    </Flex>
  );
};

type TabProps = {
  children: ReactNode;
  active: boolean;
  onTabClick: () => void;
  onTabDoubleClick: () => void;
};

const Tab = ({ children, active, onTabClick, onTabDoubleClick }: TabProps) => {
  return (
    <Box
      flex={1}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onTabDoubleClick();
      }}
      onClick={() => onTabClick()}
      sx={{
        cursor: "pointer",
        minWidth: "80px",
        maxWidth: "80px",
        width: "80px",
        textOverflow: "ellipsis",
        bgcolor: active
          ? CHART_COLORS.bgSecondaryHover
          : CHART_COLORS.surfaceWhite,
        overflow: "visible",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: CHART_DIMENSIONS.tabPx,
        py: CHART_DIMENSIONS.tabPy,
        boxSizing: "border-box",
        "&:hover": {
          bgcolor: CHART_COLORS.bgSecondaryHover,
        },
      }}
    >
      {children}
    </Box>
  );
};
